import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  RangeSlider,
  Button,
  Banner,
  Toast,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Box,
  Spinner,
  Tabs,
  Badge,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';
import { ChatWidgetSettings, defaultChatWidgetSettings } from '../types';
import { UpgradeBanner } from '../components/UpgradeBanner';

// Simple color input component
function ColorInput({ 
  label, 
  value, 
  onChange,
  helpText 
}: { 
  label: string; 
  value: string; 
  onChange: (value: string) => void;
  helpText?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input 
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ 
          width: 36, 
          height: 36, 
          padding: 0,
          border: '1px solid #ccc',
          borderRadius: 4,
          cursor: 'pointer'
        }}
      />
      <div>
        <Text as="span" variant="bodyMd">{label}</Text>
        {helpText && (
          <Text as="p" variant="bodySm" tone="subdued">{helpText}</Text>
        )}
      </div>
    </div>
  );
}

// Image upload component for custom icon - uploads to Shopify CDN
function ImageUpload({
  label,
  value,
  onChange,
  helpText,
  fetchWithAuth,
  shop
}: {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  helpText?: string;
  fetchWithAuth: <T>(url: string, options?: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown }) => Promise<T>;
  shop: string | null;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !shop) return;

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      setUploadError(t('chat.upload.fileTooLarge'));
      return;
    }
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError(t('chat.upload.invalidFile'));
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    try {
      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload to Shopify CDN via our API
      const result = await fetchWithAuth<{ url?: string; success?: boolean; error?: string }>(
        `/api/chat/widget/upload-icon`, 
        {
          method: 'POST',
          body: { base64Image: base64 },
        }
      );

      if (result.url) {
        onChange(result.url);
      } else {
        throw new Error(result.error || 'No URL returned from upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : t('chat.upload.uploadError'));
    } finally {
      setIsUploading(false);
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <BlockStack gap="200">
      <Text as="span" variant="bodyMd">{label}</Text>
      {value ? (
        <InlineStack gap="300" blockAlign="center">
          <div style={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            overflow: 'hidden',
            border: '2px solid #ccc'
          }}>
            <img 
              src={value} 
              alt="Custom icon preview" 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <Button 
            variant="plain" 
            tone="critical"
            onClick={() => onChange(undefined)}
          >
            {t('chat.upload.remove')}
          </Button>
        </InlineStack>
      ) : (
        <BlockStack gap="200">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            loading={isUploading}
            disabled={isUploading}
          >
            {isUploading ? t('chat.upload.uploading') : t('chat.upload.uploadImage')}
          </Button>
          {uploadError && (
            <Text as="p" variant="bodySm" tone="critical">{uploadError}</Text>
          )}
        </BlockStack>
      )}
      {helpText && (
        <Text as="p" variant="bodySm" tone="subdued">{helpText}</Text>
      )}
    </BlockStack>
  );
}

export default function ChatSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, shopInfo } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();

  const [settings, setSettings] = useState<ChatWidgetSettings>(defaultChatWidgetSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // Load settings
  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await fetchWithAuth<ChatWidgetSettings>('/api/chat/widget/settings');
        setSettings({ ...defaultChatWidgetSettings, ...data });
      } catch (error) {
        console.error('Error loading chat widget settings:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadSettings();
  }, [fetchWithAuth]);

  // Update setting helper
  const updateSetting = useCallback(<K extends keyof ChatWidgetSettings>(
    key: K,
    value: ChatWidgetSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  // Save settings
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await fetchWithAuth<ChatWidgetSettings>('/api/chat/widget/settings', {
        method: 'PUT',
        body: settings,
      });

      setToastMessage(t('chat.toast.saved'));
      setToastError(false);
      setHasChanges(false);
    } catch (error) {
      setToastMessage(error instanceof Error ? error.message : t('chat.toast.saveError'));
      setToastError(true);
    } finally {
      setIsSaving(false);
      setToastActive(true);
    }
  }, [fetchWithAuth, settings]);

  const tabs = [
    { id: 'general', content: t('chat.tabs.general'), panelID: 'general-panel' },
    { id: 'appearance', content: t('chat.tabs.appearance'), panelID: 'appearance-panel' },
    { id: 'messages', content: t('chat.tabs.messages'), panelID: 'messages-panel' },
    { id: 'behavior', content: t('chat.tabs.behavior'), panelID: 'behavior-panel' },
    { id: 'advanced', content: t('chat.tabs.advanced'), panelID: 'advanced-panel' },
  ];

  if (isLoading) {
    return (
      <Page title={t('chat.title')}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" blockAlign="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Plan gating: AI Chat requires Growth+
  const hasAiChat = shopInfo?.planFeatures?.hasAiChat !== false;
  if (!hasAiChat) {
    return (
      <Page
        title={t('chat.title')}
        backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      >
        <Layout>
          <Layout.Section>
            <UpgradeBanner feature="aiChat" requiredPlan="Growth" />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('chat.title')}
      subtitle={t('chat.subtitle')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
      primaryAction={{
        content: t('common.save'),
        onAction: handleSave,
        loading: isSaving,
        disabled: !hasChanges,
      }}
    >
        <Layout>
          {/* Enable/Disable Banner */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="h2" variant="headingMd">{t('chat.widget.title')}</Text>
                      <Badge tone={settings.enabled ? 'success' : 'critical'}>
                        {settings.enabled ? t('chat.widget.enabled') : t('chat.widget.disabled')}
                      </Badge>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t('chat.widget.description')}
                    </Text>
                  </BlockStack>
                  <Button
                    variant={settings.enabled ? 'secondary' : 'primary'}
                    onClick={() => updateSetting('enabled', !settings.enabled)}
                  >
                    {settings.enabled ? t('chat.widget.disable') : t('chat.widget.enable')}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Discount Codes Quick Access */}
          <Layout.Section>
            <Card>
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h2" variant="headingMd">🏷️ {t('discountCodes.title', 'Discount Codes')}</Text>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t('discountCodes.chatIntegrationDesc', 'Choose which discount codes the AI assistant can recommend to customers')}
                  </Text>
                </BlockStack>
                <Button onClick={() => navigate('/app/chat/discounts')}>
                  {t('discountCodes.manage', 'Manage Discount Codes')}
                </Button>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Settings Tabs */}
          <Layout.Section>
            <Card>
              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                <Box padding="400">
                  {/* General Tab */}
                  {selectedTab === 0 && (
                    <BlockStack gap="400">
                      <FormLayout>
                        <Select
                          label={t('chat.general.position')}
                          options={[
                            { label: t('chat.general.positionBottomRight'), value: 'bottom-right' },
                            { label: t('chat.general.positionBottomLeft'), value: 'bottom-left' },
                            { label: t('chat.general.positionTopRight'), value: 'top-right' },
                            { label: t('chat.general.positionTopLeft'), value: 'top-left' },
                          ]}
                          value={settings.position}
                          onChange={(value) => updateSetting('position', value as ChatWidgetSettings['position'])}
                          helpText={t('chat.general.positionHelp')}
                        />

                        <Select
                          label={t('chat.general.size')}
                          options={[
                            { label: t('chat.general.sizeSmall'), value: 'small' },
                            { label: t('chat.general.sizeMedium'), value: 'medium' },
                            { label: t('chat.general.sizeLarge'), value: 'large' },
                          ]}
                          value={settings.size}
                          onChange={(value) => updateSetting('size', value as ChatWidgetSettings['size'])}
                        />

                        <Select
                          label={t('chat.general.iconType')}
                          options={[
                            { label: t('chat.general.iconChat'), value: 'chat' },
                            { label: t('chat.general.iconRobot'), value: 'robot' },
                            { label: t('chat.general.iconHelp'), value: 'help' },
                            { label: t('chat.general.iconCustom'), value: 'custom' },
                          ]}
                          value={settings.iconType}
                          onChange={(value) => updateSetting('iconType', value as ChatWidgetSettings['iconType'])}
                        />

                        {settings.iconType === 'custom' && (
                          <ImageUpload
                            label={t('chat.general.iconCustom')}
                            value={settings.customIconUrl}
                            onChange={(value) => updateSetting('customIconUrl', value)}
                            helpText={t('chat.general.customIconHelp')}
                            fetchWithAuth={fetchWithAuth}
                            shop={shop}
                          />
                        )}

                        <Checkbox
                          label={t('chat.general.showBranding')}
                          checked={settings.showBranding}
                          onChange={(value) => updateSetting('showBranding', value)}
                        />
                      </FormLayout>
                    </BlockStack>
                  )}

                  {/* Appearance Tab */}
                  {selectedTab === 1 && (
                    <BlockStack gap="400">
                      <Text as="h3" variant="headingSm">{t('chat.colors.title')}</Text>
                      <InlineStack gap="400" wrap>
                        <ColorInput
                          label={t('chat.colors.primary')}
                          value={settings.primaryColor}
                          onChange={(value) => updateSetting('primaryColor', value)}
                          helpText={t('chat.colors.primaryHelp')}
                        />
                        <ColorInput
                          label={t('chat.colors.secondary')}
                          value={settings.secondaryColor}
                          onChange={(value) => updateSetting('secondaryColor', value)}
                          helpText={t('chat.colors.secondaryHelp')}
                        />
                        <ColorInput
                          label={t('chat.colors.background')}
                          value={settings.backgroundColor}
                          onChange={(value) => updateSetting('backgroundColor', value)}
                        />
                        <ColorInput
                          label={t('chat.colors.text')}
                          value={settings.textColor}
                          onChange={(value) => updateSetting('textColor', value)}
                        />
                        <ColorInput
                          label={t('chat.colors.userMessage')}
                          value={settings.userMessageColor}
                          onChange={(value) => updateSetting('userMessageColor', value)}
                        />
                        <ColorInput
                          label={t('chat.colors.assistantMessage')}
                          value={settings.assistantMessageColor}
                          onChange={(value) => updateSetting('assistantMessageColor', value)}
                        />
                      </InlineStack>

                      <Divider />

                      <Text as="h3" variant="headingSm">{t('chat.style.title')}</Text>
                      <FormLayout>
                        <Select
                          label={t('chat.style.themeMode')}
                          options={[
                            { label: t('chat.style.themeModeLight'), value: 'light' },
                            { label: t('chat.style.themeModeDark'), value: 'dark' },
                            { label: t('chat.style.themeModeAuto'), value: 'auto' },
                          ]}
                          value={settings.themeMode}
                          onChange={(value) => updateSetting('themeMode', value as ChatWidgetSettings['themeMode'])}
                        />

                        <Select
                          label={t('chat.style.animation')}
                          options={[
                            { label: t('chat.style.animSlide'), value: 'slide' },
                            { label: t('chat.style.animFade'), value: 'fade' },
                            { label: t('chat.style.animBounce'), value: 'bounce' },
                            { label: t('chat.style.animNone'), value: 'none' },
                          ]}
                          value={settings.animationType}
                          onChange={(value) => updateSetting('animationType', value as ChatWidgetSettings['animationType'])}
                        />

                        <TextField
                          label={t('chat.style.borderRadius')}
                          value={settings.borderRadius}
                          onChange={(value) => updateSetting('borderRadius', value)}
                          helpText={t('chat.style.borderRadiusHelp')}
                          autoComplete="off"
                        />

                        <TextField
                          label={t('chat.style.fontFamily')}
                          value={settings.fontFamily}
                          onChange={(value) => updateSetting('fontFamily', value)}
                          helpText={t('chat.style.fontFamilyHelp')}
                          autoComplete="off"
                        />
                      </FormLayout>
                    </BlockStack>
                  )}

                  {/* Messages Tab */}
                  {selectedTab === 2 && (
                    <BlockStack gap="400">
                      <FormLayout>
                        <TextField
                          label={t('chat.settings.headerTitle', 'Header Title')}
                          value={settings.headerTitle}
                          onChange={(value) => updateSetting('headerTitle', value)}
                          autoComplete="off"
                          placeholder={t('chat.title', 'AI Assistant')}
                          helpText={t('chat.settings.headerTitleHelp', 'Leave empty to use auto-detected language')}
                        />
                      </FormLayout>

                      <Divider />

                      <FormLayout>
                        <TextField
                          label={t('chat.welcomeMessage')}
                          value={settings.welcomeMessage}
                          onChange={(value) => updateSetting('welcomeMessage', value)}
                          multiline={2}
                          autoComplete="off"
                          placeholder={t('chat.welcomeMessagePlaceholder')}
                          helpText={t('chat.settings.welcomeMessageHelp', 'Leave empty to use auto-detected language')}
                        />
                      </FormLayout>

                      <Divider />

                      <FormLayout>
                        <TextField
                          label={t('chat.placeholderText')}
                          value={settings.inputPlaceholder}
                          onChange={(value) => updateSetting('inputPlaceholder', value)}
                          autoComplete="off"
                          placeholder={t('chat.placeholderPlaceholder')}
                          helpText={t('chat.settings.placeholderHelp', 'Leave empty to use auto-detected language')}
                        />
                      </FormLayout>
                    </BlockStack>
                  )}

                  {/* Behavior Tab */}
                  {selectedTab === 3 && (
                    <BlockStack gap="400">
                      <FormLayout>
                        <Checkbox
                          label={t('chat.behaviorSettings.autoOpen')}
                          checked={settings.autoOpen}
                          onChange={(value) => updateSetting('autoOpen', value)}
                          helpText={t('chat.behaviorSettings.autoOpenHelp')}
                        />

                        {settings.autoOpen && (
                          <RangeSlider
                            label={t('chat.behaviorSettings.autoOpenDelay', { seconds: settings.autoOpenDelay / 1000 })}
                            value={settings.autoOpenDelay}
                            min={1000}
                            max={30000}
                            step={1000}
                            onChange={(value) => updateSetting('autoOpenDelay', value as number)}
                          />
                        )}

                        <Checkbox
                          label={t('chat.behaviorSettings.showOnMobile')}
                          checked={settings.showOnMobile}
                          onChange={(value) => updateSetting('showOnMobile', value)}
                        />

                        {settings.showOnMobile && (
                          <TextField
                            label={t('chat.behaviorSettings.mobileBreakpoint')}
                            type="number"
                            value={String(settings.mobileBreakpoint)}
                            onChange={(value) => updateSetting('mobileBreakpoint', parseInt(value) || 768)}
                            helpText={t('chat.behaviorSettings.mobileBreakpointHelp')}
                            autoComplete="off"
                          />
                        )}

                        <Checkbox
                          label={t('chat.behaviorSettings.soundEnabled')}
                          checked={settings.soundEnabled}
                          onChange={(value) => updateSetting('soundEnabled', value)}
                          helpText={t('chat.behaviorSettings.soundEnabledHelp')}
                        />
                      </FormLayout>
                    </BlockStack>
                  )}

                  {/* Advanced Tab */}
                  {selectedTab === 4 && (
                    <BlockStack gap="400">
                      <Banner tone="info">
                        {t('chat.advanced.banner')}
                      </Banner>

                      <FormLayout>
                        <FormLayout.Group>
                          <TextField
                            label={t('chat.advanced.buttonSize')}
                            type="number"
                            value={String(settings.buttonSize)}
                            onChange={(value) => updateSetting('buttonSize', parseInt(value) || 60)}
                            autoComplete="off"
                          />
                          <TextField
                            label={t('chat.advanced.windowWidth')}
                            type="number"
                            value={String(settings.windowWidth)}
                            onChange={(value) => updateSetting('windowWidth', parseInt(value) || 380)}
                            autoComplete="off"
                          />
                          <TextField
                            label={t('chat.advanced.windowHeight')}
                            type="number"
                            value={String(settings.windowHeight)}
                            onChange={(value) => updateSetting('windowHeight', parseInt(value) || 520)}
                            autoComplete="off"
                          />
                        </FormLayout.Group>

                        <FormLayout.Group>
                          <TextField
                            label={t('chat.advanced.offsetX')}
                            type="number"
                            value={String(settings.offsetX)}
                            onChange={(value) => updateSetting('offsetX', parseInt(value) || 20)}
                            helpText={t('chat.advanced.offsetXHelp')}
                            autoComplete="off"
                          />
                          <TextField
                            label={t('chat.advanced.offsetY')}
                            type="number"
                            value={String(settings.offsetY)}
                            onChange={(value) => updateSetting('offsetY', parseInt(value) || 20)}
                            autoComplete="off"
                          />
                          <TextField
                            label={t('chat.advanced.zIndex')}
                            type="number"
                            value={String(settings.zIndex)}
                            onChange={(value) => updateSetting('zIndex', parseInt(value) || 9999)}
                            helpText={t('chat.advanced.zIndexHelp')}
                            autoComplete="off"
                          />
                        </FormLayout.Group>
                      </FormLayout>

                      <Divider />

                      <Text as="h3" variant="headingSm">{t('chat.advanced.suggestedQuestionsTitle')}</Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t('chat.advanced.suggestedQuestionsDesc')}
                      </Text>

                      <FormLayout>
                        <Checkbox
                          label={t('chat.advanced.showSuggestions')}
                          checked={settings.showSuggestions}
                          onChange={(value) => updateSetting('showSuggestions', value)}
                          helpText={t('chat.advanced.showSuggestionsHelp')}
                        />
                        {settings.showSuggestions && (
                          <>
                            <Checkbox
                              label={t('chat.advanced.autoSuggest')}
                              checked={settings.autoSuggestEnabled}
                              onChange={(value) => updateSetting('autoSuggestEnabled', value)}
                              helpText={t('chat.advanced.autoSuggestHelp')}
                            />
                            {settings.autoSuggestEnabled && (
                              <Select
                                label={t('chat.advanced.suggestCount')}
                                options={[
                                  { label: t('chat.advanced.suggest1'), value: '1' },
                                  { label: t('chat.advanced.suggest2'), value: '2' },
                                  { label: t('chat.advanced.suggest3'), value: '3' },
                                ]}
                                value={String(settings.autoSuggestCount)}
                                onChange={(value) => updateSetting('autoSuggestCount', parseInt(value))}
                                helpText={t('chat.advanced.suggestCountHelp')}
                              />
                            )}
                            {!settings.autoSuggestEnabled && (
                              <TextField
                                label={t('chat.advanced.customQuestions')}
                                value={settings.suggestedQuestions.join('\n')}
                                onChange={(value) => updateSetting('suggestedQuestions', value.split('\n').filter(q => q.trim()))}
                                multiline={3}
                                autoComplete="off"
                                helpText={t('chat.advanced.customQuestionsHelp')}
                              />
                            )}
                          </>
                        )}
                      </FormLayout>
                    </BlockStack>
                  )}
                </Box>
              </Tabs>
            </Card>
          </Layout.Section>

          {/* Preview Section */}
          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">{t('chat.livePreview')}</Text>
                <Box
                  background="bg-surface-secondary"
                  padding="400"
                  borderRadius="200"
                  minHeight="420px"
                >
                  {/* Mini chat window preview */}
                  <div style={{ 
                    width: '100%',
                    maxWidth: '280px',
                    margin: '0 auto',
                    backgroundColor: settings.backgroundColor,
                    borderRadius: settings.borderRadius,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    fontFamily: settings.fontFamily
                  }}>
                    {/* Header */}
                    <div style={{
                      backgroundColor: settings.primaryColor,
                      color: settings.secondaryColor,
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>
                        {settings.headerTitle || 'Shopping Assistant'}
                      </span>
                      <span style={{ opacity: 0.8, fontSize: '18px' }}>×</span>
                    </div>
                    
                    {/* Messages */}
                    <div style={{ 
                      padding: '12px', 
                      minHeight: '200px',
                      backgroundColor: settings.backgroundColor 
                    }}>
                      {/* Welcome message */}
                      <div style={{
                        backgroundColor: settings.assistantMessageColor,
                        color: settings.textColor,
                        padding: '10px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        marginBottom: '12px',
                        maxWidth: '85%'
                      }}>
                        {settings.welcomeMessage || 'Hey there! 👋 Looking for something special?'}
                      </div>
                      
                      {/* Sample user message */}
                      <div style={{
                        backgroundColor: settings.userMessageColor,
                        color: settings.secondaryColor,
                        padding: '10px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        marginBottom: '12px',
                        maxWidth: '85%',
                        marginLeft: 'auto'
                      }}>
                        Hi! What do you recommend?
                      </div>
                      
                      {/* Sample assistant response */}
                      <div style={{
                        backgroundColor: settings.assistantMessageColor,
                        color: settings.textColor,
                        padding: '10px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        maxWidth: '85%'
                      }}>
                        Great choice! Check out our best sellers...
                      </div>
                    </div>
                    
                    {/* Suggestions */}
                    {settings.showSuggestions && (
                      <div style={{ 
                        padding: '8px 12px', 
                        borderTop: `1px solid ${settings.assistantMessageColor}`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px'
                      }}>
                        {['Any deals?', 'Best sellers'].slice(0, settings.autoSuggestCount).map((q, i) => (
                          <span key={i} style={{
                            backgroundColor: settings.assistantMessageColor,
                            color: settings.textColor,
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}>{q}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Input */}
                    <div style={{ 
                      padding: '10px 12px', 
                      borderTop: `1px solid ${settings.assistantMessageColor}`,
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <div style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '16px',
                        fontSize: '12px',
                        color: '#999'
                      }}>
                        {settings.inputPlaceholder || 'Ask me anything...'}
                      </div>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: settings.primaryColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={settings.secondaryColor}>
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Branding */}
                    {settings.showBranding && (
                      <div style={{ 
                        padding: '6px', 
                        textAlign: 'center', 
                        fontSize: '10px', 
                        color: '#999',
                        borderTop: `1px solid ${settings.assistantMessageColor}`
                      }}>
                        Powered by NexusAI
                      </div>
                    )}
                  </div>
                  
                  {/* Chat button */}
                  <div style={{
                    marginTop: '16px',
                    display: 'flex',
                    justifyContent: settings.position.includes('right') ? 'flex-end' : 'flex-start'
                  }}>
                    <div
                      style={{
                        width: `${settings.buttonSize}px`,
                        height: `${settings.buttonSize}px`,
                        borderRadius: '50%',
                        backgroundColor: settings.primaryColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Chat icon */}
                      {settings.iconType === 'chat' && (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill={settings.secondaryColor}>
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
                          <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
                        </svg>
                      )}
                      {/* Robot icon */}
                      {settings.iconType === 'robot' && (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill={settings.secondaryColor}>
                          <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2M7.5 13A2.5 2.5 0 0 0 5 15.5A2.5 2.5 0 0 0 7.5 18a2.5 2.5 0 0 0 2.5-2.5A2.5 2.5 0 0 0 7.5 13m9 0a2.5 2.5 0 0 0-2.5 2.5a2.5 2.5 0 0 0 2.5 2.5a2.5 2.5 0 0 0 2.5-2.5a2.5 2.5 0 0 0-2.5-2.5z"/>
                        </svg>
                      )}
                      {/* Help icon */}
                      {settings.iconType === 'help' && (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill={settings.secondaryColor}>
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                        </svg>
                      )}
                      {/* Custom icon */}
                      {settings.iconType === 'custom' && settings.customIconUrl && (
                        <img 
                          src={settings.customIconUrl} 
                          alt="Chat icon" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }} 
                        />
                      )}
                      {/* Fallback if custom but no URL */}
                      {settings.iconType === 'custom' && !settings.customIconUrl && (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill={settings.secondaryColor}>
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
                          <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </Box>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t('chat.previewDescription')}
                </Text>
              </BlockStack>
            </Card>

            {/* Installation Instructions */}
            <Box paddingBlockStart="400">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">{t('chat.installation')}</Text>
                  <Text as="p" variant="bodySm">
                    {t('chat.installationDesc')}
                  </Text>
                  <Button
                    url={`https://${shop}/admin/themes/current/editor`}
                    external
                  >
                    {t('chat.openThemeEditor')}
                  </Button>
                </BlockStack>
              </Card>
            </Box>
          </Layout.Section>
        </Layout>

      {toastActive && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setToastActive(false)}
        />
      )}
    </Page>
  );
}
